import React, { useContext, useState } from "react";
import { useNavigate } from "react-router";
import { isNil } from "lodash";
import { useEffectOnce } from "@lib-shared/hooks";
import { CachedItem } from "@lib-shared/lib";
import { appSettings } from "core/config";
import { BusinessPage, Key } from "core/constants";
import { SelfServiceContext } from "core/context/self-service.context";
import { useUpdatePage } from "core/hooks";
import { sessionManager } from "core/instances";
import LocalStorageHelper from "core/lib/local-storage-helper";
import Style from "core/lib/style";
import UrlUtils from "core/lib/url-utils";
import UserActivityLoggingApi from "core/services/user-activity-logging.service";
import { activityLogArea } from "core/services/user-tracking-helper";
import { LightSpinner } from "components/common-ui";

const returnUrlCache = new CachedItem(Key.returnUrl, true, 0, false);

const SwitchCompany = ({ display = true }: { display?: boolean }) => {
    const [updateTitle, updateBodyCssName] = useUpdatePage();
    const [isLoading, setIsLoading] = useState(false);
    const [companyDetails, setCompanyDetails] = useState<any>(null);

    const selfServiceContext = useContext(SelfServiceContext);

    const navigate = useNavigate();
    const pageTitle = "Choose Company";

    useEffectOnce(() => {
        updateBodyCssName(`${Style.auth} company-switcher`);
        updateTitle(pageTitle);
    });

    const redirectToLogin = () => {
        const returnUrl = window.location.hash.substring(1);
        console.log(`Return url ${returnUrl}`);
        LocalStorageHelper.clearLog();

        sessionManager.signIn(returnUrl);
    }

    const checkAccess = async () => {
        const access = await sessionManager.hasAccessAsync();

        if (!access) {
            redirectToLogin();
            return false;
        }

        return true;
    }

    const getCustomerDetails = () => {
        const user = sessionManager.loggedInUser;

        const details = user?.companies
            .map(x => ({ id: x.customerNumber, name: x.companyName?.toTitleCase() }))
            .filter((x: { name: string; }) => !isNil(x.name));

        if (details.length === 1) {
            navigate(BusinessPage.dashboard);
            return;
        }

        return details;
    }

    useEffectOnce(() => {
        UrlUtils.removeQueryParamsFromUrl();

        if (!appSettings.isEnabled.companySwitching) {
            navigate(BusinessPage.dashboard);
            return;
        }

        setIsLoading(true);

        const onLoad = async () => {
            const hasAccess = await checkAccess();

            if (!hasAccess) {
                return;
            }

            const details = getCustomerDetails();

            setCompanyDetails(details);
        }

        onLoad();

        setIsLoading(false);
    });

    const getReturnUrl = () => {
        let returnUrl = returnUrlCache.value;

        if (!returnUrl?.includes("#")) {
            return returnUrl;
        }

        returnUrl = returnUrl.split("#")[1];
        return returnUrl.startsWith("/") ? returnUrl : `/${returnUrl}`;
    }

    const switchCompany = (company: { id: number; }) => {
        sessionManager.switchCompany(company.id);

        new UserActivityLoggingApi().logActivityAsync(activityLogArea.auth, "Switch", company);

        const returnUrl = getReturnUrl();

        returnUrlCache.clear();

        selfServiceContext.reset();

        LocalStorageHelper.cleanUp();

        selfServiceContext.populateAll();

        navigate(isNil(returnUrl) ? BusinessPage.dashboard : returnUrl);
    }

    const company = (x: { name: string | undefined; id: number; }, ix: React.Key | null | undefined) => {
        return <li key={ix} onClick={(e) => switchCompany(x)}>
            <i className="icon-walk-in-centres_thin" />
            <label title={x.name}>{x.name ?? x.id}</label>
        </li>;
    }

    if (display === false) {
        return null;
    }

    if (isNil(companyDetails) || isLoading) {
        return <LightSpinner isCentered={true} />;
    }

    return (
        <section className="company-switcher">
            <p>Which of your companies<br />would you like to manage?</p>

            <ul className="company-list">
                {companyDetails.map((x: any, ix: string) => company(x, ix))}
            </ul>
        </section>
    );
}

export default SwitchCompany;
